import React from 'react';

import { MenuItem } from '@mui/material';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { Risk } from 'api/queries/getRiskRegister.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { Separator } from 'components/common/WidgetWithTitle/Separator/separator';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useRiskRegisterLogic } from 'components/widgets/RiskRegister/RiskRegister.logic';
import classes from 'components/widgets/RiskRegister/RiskRegister.module.scss';
import { useRiskRegisterProvider } from 'components/widgets/RiskRegister/RiskRegister.provider';
import { columnsV8, initialState } from 'components/widgets/RiskRegister/RiskRegister.utils';
import { CreateRiskRegisterButton } from 'components/widgets/RiskRegister/components/CreateRiskRegisterButton/CreateRiskRegisterButton';
import { DeleteRiskRegisterButton } from 'components/widgets/RiskRegister/components/DeleteRiskRegisterButton/DeleteRiskRegisterButton';
import { ExportToCsvButton } from 'components/widgets/RiskRegister/components/ExportToCsvButton/ExportToCsvButton';
import { Filters } from 'components/widgets/RiskRegister/components/Filters/Filters';
import { RiskRegisterRoutes } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/RiskRegisterRoutes';
import { RunQsra } from 'components/widgets/RiskRegister/components/RunQsra/RunQsra';
import { UploadRisksButton } from 'components/widgets/RiskRegister/components/UploadRisksButton/UploadRisksButton';

export const RiskRegister = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    setSubTitleForExport,
    activeTab,
}) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().riskRegister;

    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    // Provider
    const { ui, api } = useRiskRegisterProvider({
        widgetId,
        contractId,
        projectId,
        sorting: table.sorting,
        globalFilterData,
        latestVersionId,
        endpoints,
    });

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: configTitle,
        setSubTitleForExport,
    });

    const { logic } = useRiskRegisterLogic({
        ui,
        api,
        table,
    });

    return activeTab === 'table' ? (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={configTitle} postfix={`(${logic.rowCount})`} />}
            titleFilters={[
                <div className={classes.titleFilters} key={'filters'}>
                    <Filters ui={ui} />
                    <DeleteRiskRegisterButton table={table} />
                </div>,
            ]}
            titleComponents={[
                <div key={'actions'} className={classes.actions}>
                    <RunQsra key={'runQsra'} latestVersionId={latestVersionId} tableRef={table.tableRef} />
                    <CreateRiskRegisterButton key={'create'} />
                </div>,

                <Separator key={'separator'} className={classes.separator} />,
                <UploadRisksButton key={'uploadRisksButton'} />,

                <ExportDropdown key={'export'} title={configTitle} subTitle={subTitle}>
                    <MenuItem>
                        <ExportToCsvButton
                            headers={logic.exportCsv.headers}
                            rows={logic.exportCsv.rows}
                            fileName={logic.exportCsv.fileName}
                        />
                    </MenuItem>
                </ExportDropdown>,
                <ColumnsManager
                    onOpenModal={() => table.setModalColumnsOpen(true)}
                    onHideModal={() => table.setModalColumnsOpen(false)}
                    showModal={table.modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={table.tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                    onChangeOrder={table.setColumnOrder}
                    onChangeVisibility={table.setColumnVisibility}
                />,
                <TooltipIcon key={'info'} tooltip={api.riskRegister.instance.data?.info} />,
                <ThreeDotsMenu widgetId={widgetId} componentKey={componentKey} shareTitle={configTitle} shareLink={route} />,
            ]}
        >
            <TableContainer>
                {table.updatedInitialState && (
                    <TableV8<Risk>
                        data={api.riskRegister.instance.data?.riskList || []}
                        columns={columnsV8}
                        isLoading={api.riskRegister.instance.isFetching}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualSorting={false}
                        manualPagination={false}
                        manualFiltering={false}
                        columnFilters={table.columnFilters}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                        ref={table.tableRef}
                        totalPages={table.totalPages}
                        pagination={table.pagination}
                        setPagination={table.setPagination}
                        isPaginationDisabled={api.riskRegister.instance.isFetching}
                        displayPaginationBottomBar
                    />
                )}
            </TableContainer>

            <RiskRegisterRoutes />
        </WidgetWithTitle>
    ) : null;
};
